import { duration, formatDate, isSame, now } from "common";
import { Dayjs } from "dayjs";
import { compact, omit, unionBy } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { AllowedPrivacy, DisplayType, ProductObject, PurchaseItemPrivacy, Timeslot } from "types";
import { useDebounce } from "use-debounce";
import { queryClient } from "@/api/queryClient";
import { flattenInfiniteData, generateQueryKey } from "@/hooks/helper";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { usePackage } from "@/hooks/usePackage";
import { getProductIdsInPackage } from "@/models/Package";

export enum DateOffset {
    ZERO_DAYS = 0,
    THIRTY_ONE_DAYS = 31,
    SIXTY_TWO_DAYS = 62,
}

interface useArrivalTimeslotsProps {
    product: ID;
    quantity: number;
    start: Dayjs;
    showAllTimeslots?: boolean;
    isPastAvailabilityChecked?: boolean;
    isOverrideFutureCutoffChecked?: boolean;
    privacy?: AllowedPrivacy | PurchaseItemPrivacy;
    timezone?: string;
    packageId?: ID;
    offsetDate: { start: number; end: number };
}

const getDateRange = (
    date: Dayjs,
    isPastAvailabilityChecked: boolean,
    offsetDate: { start: DateOffset; end: DateOffset },
) => {
    const start = date.startOf("month").add(offsetDate.start, "days");
    const end = date.add(offsetDate.end, "days");

    if (isPastAvailabilityChecked) {
        return { start, end };
    }

    if (date.startOf("day").isBefore(now()) && end.isAfter(now())) {
        // This is for situations when you're changing arrival, and the arrival date is after today's date but still
        // in the current month
        return { start: now(), end };
    }

    return { start, end };
};

export const useArrivalTimeslots = ({
    product,
    quantity,
    start,
    showAllTimeslots = false,
    isPastAvailabilityChecked = false,
    isOverrideFutureCutoffChecked = false,
    privacy,
    timezone,
    packageId,
    offsetDate,
}: useArrivalTimeslotsProps) => {
    // We use this ref to track the user's first click on the 'Show past availability' toggle.
    // When the user clicks on the toggle for the first time, only timeslots from the beginning of the month to the current date will be loaded.
    // The next clicks on the toggle will reload all timeslots for 2 months.
    const pastAvailabilityRef = useRef({ name: "pastAvailability", isFirstToggle: true, clearCache: true });
    const overrideFutureCutoffRef = useRef({ name: "overrideFutureCutoff", isFirstToggle: true, clearCache: true });

    useEffect(() => {
        const handleToggle = (isChecked: boolean, ref: typeof pastAvailabilityRef) => {
            if (isChecked && ref.current.isFirstToggle) {
                ref.current.isFirstToggle = false;
            } else if (!isChecked && ref.current.clearCache && !ref.current.isFirstToggle) {
                eraseArrivalCache();
                ref.current.clearCache = false;
            }
        };

        handleToggle(isPastAvailabilityChecked, pastAvailabilityRef);
        handleToggle(isOverrideFutureCutoffChecked, overrideFutureCutoffRef);
    }, [isPastAvailabilityChecked, isOverrideFutureCutoffChecked]);

    const seller = useSeller();
    const { datepickerPreference: datePickerPreference } = seller.preferences;
    const needsUnits = datePickerPreference.displayType === DisplayType.PRICE;

    const { pkg } = usePackage(packageId);
    const formattedStart = formatDate(start);

    const payload = useMemo(() => {
        // extending end date to avoid flickering of upcoming dates block inside the date picker while loading timeslots
        const { start: startParam, end: endParam } = getDateRange(
            start,
            isPastAvailabilityChecked,
            // if the user clicks on the toggle for the first time while on the current month,
            // then only timeslots up to today will be fetched
            isPastAvailabilityChecked && pastAvailabilityRef.current.isFirstToggle && isSame(start, now())
                ? { start: DateOffset.ZERO_DAYS, end: DateOffset.ZERO_DAYS }
                : offsetDate,
        );

        const payloadObject: any = {
            start: [formatDate(startParam), formatDate(endParam)].join(","),
            product,
            limit: 500,
        };
        // units.open is cheap (equipment unit counts only, no purchase-rule pricing) and no-ops for non-equipment products
        const include = ["capacity.max", "units.open"];
        if (needsUnits) {
            // We only need units if the date picker is configured to show prices, otherwise it's a performance hit
            include.push("units");
        }

        payloadObject.include = include.join(",");
        if (pkg && pkg.object !== ProductObject.PARTNER_PACKAGE) {
            const selectedExperienceIds = getProductIdsInPackage(pkg);
            payloadObject.product = selectedExperienceIds.join(",");
            payloadObject.package = packageId; // We need this so that package blackout schedules are considered
        }

        if (privacy) {
            payloadObject.privacy = privacy;
        }

        // For an existing purchase, showAllTimeslots is always false which doesn't give response for past dates
        if (!showAllTimeslots && !isPastAvailabilityChecked && quantity - 1 >= 0) {
            // use underscore to keep it consistent with the API. If you use a dot, then API will return underscore in the pagination url
            payloadObject["capacity_open[gt]"] = quantity - 1; // Because capacity endpoint does not have a gte operator
        }

        if (isPastAvailabilityChecked) {
            // This is so that the API returns the proper capacity.open. Without this, it will return zero
            payloadObject.allowPastSlots = true;
        }

        if (isOverrideFutureCutoffChecked) {
            payloadObject.overrideFutureCutoff = true;
        }

        if (timezone) {
            // This is used for virtual experiences only. However, the API does not check if the experience is a virtual
            // listing. So don't send this if the experience isn't virtual because it may change the timestamps
            payloadObject.timezone = timezone;
        }

        return payloadObject;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formattedStart,
        product,
        isPastAvailabilityChecked,
        isOverrideFutureCutoffChecked,
        privacy,
        timezone,
        pkg,
        offsetDate.start,
    ]); // Update when date/privacy/timezone changes

    // We only do quantity related calc initially or when isPastAvailabilityChecked is changes, no need to refetch on dem quantity change, that is handled by timeslotsOfDay
    const [debouncedPayload] = useDebounce(payload, 300); // Arrival picker should be quick so use the bare minimum debounce interval

    // 1) Combine the results into one key that does not have the start date limitation so we return this
    // date for any date range call. Added in X2-9385.
    // 2) Since we are not triggering a new payload on the quantity change,
    // we need to omit capacity as well, otherwise, it will reset query data on the start date change,
    // and fetch the next slots with 31 - 62 offset, this will cause timeslots for the next month after the picked date to be missing. Added in X2-9747.
    // 3) Since future dates does not change when the 'allowPastSlots' changes,
    // we don't need new query key so we could preserve future, already fetched slots, when toggling allow past dates for the 1st time to increase initial performance.
    const productKey = pkg ? `${pkg.id}` : product;
    const customKey = generateQueryKey(
        ["timeslots", "collection", "arrival", productKey],
        omit(debouncedPayload, [
            "product", // It is already in the key above
            "start",
            "capacity_open[gt]",
            !pastAvailabilityRef.current.isFirstToggle && !pastAvailabilityRef.current.clearCache
                ? ""
                : "allowPastSlots",
            !overrideFutureCutoffRef.current.isFirstToggle && !overrideFutureCutoffRef.current.clearCache
                ? ""
                : "overrideFutureCutoff",
        ]),
    );

    const customTimeslotData = getArrivalData(productKey, customKey);
    const query = useAutoFetchCollection<Timeslot>("timeslots", debouncedPayload, {
        keepPreviousData: true,
        // Only re-fetch if the change happens after this time. Shorter value for packages because you can switch between date pickers faster
        staleTime: duration(packageId ? 30 : 30, "seconds"),
        onSuccess: (data) => {
            const newData = flattenInfiniteData(data);
            const final = unionBy(newData, customTimeslotData, (t: Timeslot) => {
                // We don't de-dupe via 'id' because when a timeslot gets an event, the event ID gets added to the ID
                // This will cause problem when a new booking comes
                return `${t.arrivalDatetime.format()}_${t.privacy}_${t.product}_${t.canBook}`;
            });
            // We store this data as a plain array instead of the paginated format of useInfiniteCollection
            queryClient.setQueryData(customKey, final);
        },
    });

    return { ...query, data: customTimeslotData ?? query.data };
};

// This will give us the latest arrival data from the key. It will also implement manual cache invalidation using the
// cache key's staleTime. This is because there is no `queryFn` associated with it so RQ will never get a chance to
// invalidate the cache.
export const getArrivalData = (productId: string, cacheKey: any) => {
    const staleTimeMs = duration(15, "seconds"); // Same as main timeslots API call
    queryClient.setQueryDefaults(cacheKey, { staleTime: staleTimeMs });

    const data = queryClient.getQueryData<Timeslot[]>(cacheKey);
    if (!data) {
        return;
    }

    // Check the cache, and if it's invalidated or older than 15s (staleTimeDuration above), clear it.
    const stats = queryClient.getQueryState(cacheKey);
    if (data.length > 0 && stats) {
        // If dataUpdatedAt was more than staleTimeMs, manually clear the cache
        const { dataUpdatedAt = Date.now(), isInvalidated = false } = stats;
        if (isInvalidated || now().diff(dataUpdatedAt, "ms") > staleTimeMs) {
            setTimeout(() => {
                eraseArrivalCache(productId);
                // queryClient.removeQueries(cacheKey);
            }, 100);
        }
    }

    return data;
};

export const invalidateArrivalCache = (productId: string | string[] | null = null) => {
    queryClient.invalidateQueries(compact(["timeslots", "collection", "arrival", productId]));
    queryClient.invalidateQueries(compact(["timeslots", "collection", "upcoming", productId]));
};

// This must be done in the RAREST of circumstances. Please get a code review from @rushi before you use this method
export const eraseArrivalCache = (productId: string | string[] | null = null) => {
    console.debug(`🚨 Deleted data for ${productId}`);
    queryClient.removeQueries(compact(["timeslots", "collection", "arrival", productId]));
    queryClient.removeQueries(compact(["timeslots", "collection", "upcoming", productId]));
};
